import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import ScienceIcon from "@mui/icons-material/Science";
import TuneIcon from "@mui/icons-material/Tune";
import { api, jsonBody } from "../api";
import { PageHeader } from "../components/PageHeader";
import type { AzureAccount, AzureAccountCategory } from "../types";

export function AzurePoolPage() {
  const [accounts, setAccounts] = useState<AzureAccount[]>([]);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [dialog, setDialog] = useState<{
    category: AzureAccountCategory;
    account?: AzureAccount;
  } | null>(null);
  const [busy, setBusy] = useState("");
  const load = async () => {
    try {
      const result = await api<{ accounts: AzureAccount[] }>(
        "/admin/azure-accounts",
      );
      setAccounts(result.accounts);
    } catch (error) {
      setMessage({
        type: "error",
        text:
          error instanceof Error
            ? error.message
            : "Unable to load Azure accounts",
      });
    }
  };
  useEffect(() => {
    void load();
  }, []);

  const reorder = async (
    category: AzureAccountCategory,
    ordered: AzureAccount[],
  ) => {
    setAccounts((all) => [
      ...all.filter((a) => a.category !== category),
      ...ordered,
    ]);
    try {
      await api("/admin/azure-accounts/reorder", {
        method: "POST",
        body: jsonBody({ category, accountIds: ordered.map((a) => a.id) }),
      });
      await load();
    } catch (error) {
      setMessage({
        type: "error",
        text: error instanceof Error ? error.message : "Reorder failed",
      });
      await load();
    }
  };

  const action = async (
    account: AzureAccount,
    name: "test" | "enable" | "disable",
  ) => {
    let body: string | undefined;
    if (name === "disable") {
      const reason = window.prompt(
        "Reason for manually disabling this account:",
      );
      if (!reason) return;
      body = jsonBody({ reason });
    }
    setBusy(account.id);
    setMessage(null);
    try {
      const result = await api<{ durationMs?: number }>(
        `/admin/azure-accounts/${account.id}/actions/${name}`,
        { method: "POST", body },
      );
      setMessage({
        type: "success",
        text:
          name === "test"
            ? `Connection succeeded in ${result.durationMs} ms`
            : `Account ${name}d`,
      });
      await load();
    } catch (error) {
      setMessage({
        type: "error",
        text: error instanceof Error ? error.message : "Action failed",
      });
      await load();
    } finally {
      setBusy("");
    }
  };

  const adjust = async (account: AzureAccount) => {
    const pagesText = window.prompt(
      "Page adjustment. Use a negative number to reduce tracked usage:",
      "0",
    );
    if (!pagesText) return;
    const pages = Number(pagesText);
    if (!Number.isInteger(pages) || pages === 0) {
      setMessage({
        type: "error",
        text: "Adjustment must be a non-zero whole number",
      });
      return;
    }
    const reason = window.prompt("Reason for this usage adjustment:");
    if (!reason) return;
    try {
      await api(`/admin/azure-accounts/${account.id}/usage-adjustments`, {
        method: "POST",
        body: jsonBody({ pages, reason }),
      });
      setMessage({ type: "success", text: "Usage adjusted" });
      await load();
    } catch (error) {
      setMessage({
        type: "error",
        text: error instanceof Error ? error.message : "Adjustment failed",
      });
    }
  };

  return (
    <>
      <PageHeader
        title="Azure account pool"
        subtitle="Free resources are consumed in priority order before paid fallback. Drag cards to change priority."
      />
      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}
      <Stack gap={3}>
        {(["FREE", "PAID"] as const).map((category) => (
          <PoolSection
            key={category}
            category={category}
            accounts={accounts
              .filter((account) => account.category === category)
              .sort((a, b) => a.priority_position - b.priority_position)}
            busy={busy}
            onAdd={() => setDialog({ category })}
            onEdit={(account) => setDialog({ category, account })}
            onAction={action}
            onAdjust={adjust}
            onReorder={(ordered) => void reorder(category, ordered)}
          />
        ))}
      </Stack>
      {dialog && (
        <AccountDialog
          value={dialog}
          onClose={() => setDialog(null)}
          onSaved={async () => {
            setDialog(null);
            setMessage({
              type: "success",
              text: dialog.account
                ? "Azure account updated"
                : "Azure account added",
            });
            await load();
          }}
        />
      )}
    </>
  );
}

function PoolSection({
  category,
  accounts,
  busy,
  onAdd,
  onEdit,
  onAction,
  onAdjust,
  onReorder,
}: {
  category: AzureAccountCategory;
  accounts: AzureAccount[];
  busy: string;
  onAdd: () => void;
  onEdit: (account: AzureAccount) => void;
  onAction: (
    account: AzureAccount,
    action: "test" | "enable" | "disable",
  ) => void;
  onAdjust: (account: AzureAccount) => void;
  onReorder: (accounts: AzureAccount[]) => void;
}) {
  const [dragged, setDragged] = useState<string | null>(null);
  const move = (from: number, to: number) => {
    if (to < 0 || to >= accounts.length || from === to) return;
    const next = [...accounts],
      [item] = next.splice(from, 1);
    next.splice(to, 0, item!);
    onReorder(next);
  };
  return (
    <Card>
      <CardContent>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ sm: "center" }}
          gap={1}
          mb={2}
        >
          <Box>
            <Typography variant="h6">
              {category === "FREE" ? "Free accounts" : "Paid accounts"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {accounts.length} configured
            </Typography>
          </Box>
          <Button startIcon={<AddIcon />} variant="outlined" onClick={onAdd}>
            Add {category === "FREE" ? "Free" : "Paid"}
          </Button>
        </Stack>
        {!accounts.length ? (
          <Alert severity="info">
            No {category.toLowerCase()} Azure accounts configured.
          </Alert>
        ) : (
          <Stack gap={1.5}>
            {accounts.map((account, index) => {
              const used =
                account.successful_pages +
                account.failed_pages +
                account.reserved_pages +
                account.adjusted_pages;
              const percent = account.monthly_capacity_pages
                ? Math.max(
                    0,
                    Math.min(
                      100,
                      (used * 100) / account.monthly_capacity_pages,
                    ),
                  )
                : 0;
              return (
                <Card
                  key={account.id}
                  variant="outlined"
                  draggable
                  onDragStart={() => setDragged(account.id)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={() => {
                    const from = accounts.findIndex((a) => a.id === dragged);
                    move(from, index);
                    setDragged(null);
                  }}
                  sx={{ opacity: dragged === account.id ? 0.55 : 1 }}
                >
                  <CardContent sx={{ "&:last-child": { pb: 2 } }}>
                    <Stack
                      direction={{ xs: "column", lg: "row" }}
                      alignItems={{ lg: "center" }}
                      gap={2}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        gap={0.5}
                        minWidth={{ lg: 270 }}
                      >
                        <DragIndicatorIcon
                          color="disabled"
                          sx={{ cursor: "grab" }}
                        />
                        <Typography color="text.secondary" width={24}>
                          {index + 1}
                        </Typography>
                        <Box minWidth={0}>
                          <Typography fontWeight={750} noWrap>
                            {account.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                          >
                            {account.endpoint}
                          </Typography>
                        </Box>
                      </Stack>
                      <Box flex={1} minWidth={180}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption">This month</Typography>
                          <Typography variant="caption" fontWeight={700}>
                            {used}
                            {account.monthly_capacity_pages
                              ? ` / ${account.monthly_capacity_pages} pages`
                              : " pages"}
                          </Typography>
                        </Stack>
                        {account.monthly_capacity_pages ? (
                          <LinearProgress
                            value={percent}
                            variant="determinate"
                            color={percent >= 90 ? "warning" : "primary"}
                            sx={{ mt: 0.7, height: 7, borderRadius: 5 }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No local capacity cap
                          </Typography>
                        )}
                      </Box>
                      <Stack minWidth={{ lg: 190 }}>
                        <Chip
                          size="small"
                          color={statusColor(account.operational_status)}
                          label={account.operational_status.replaceAll(
                            "_",
                            " ",
                          )}
                          sx={{ alignSelf: "flex-start" }}
                        />
                        {account.stop_reason_detail && (
                          <Typography variant="caption" color="error" mt={0.5}>
                            {account.stop_reason_detail}
                          </Typography>
                        )}
                        {account.last_quota_exhausted_at && (
                          <Typography variant="caption" color="text.secondary">
                            Last exhausted:{" "}
                            {formatDate(account.last_quota_exhausted_at)}
                          </Typography>
                        )}
                      </Stack>
                      <Stack direction="row">
                        <Tooltip title="Move up">
                          <span>
                            <IconButton
                              disabled={index === 0}
                              onClick={() => move(index, index - 1)}
                            >
                              <ArrowUpwardIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Move down">
                          <span>
                            <IconButton
                              disabled={index === accounts.length - 1}
                              onClick={() => move(index, index + 1)}
                            >
                              <ArrowDownwardIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Test connection">
                          <span>
                            <IconButton
                              disabled={busy === account.id}
                              onClick={() => onAction(account, "test")}
                            >
                              <ScienceIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Adjust tracked usage">
                          <IconButton onClick={() => onAdjust(account)}>
                            <TuneIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton onClick={() => onEdit(account)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={account.manual_enabled ? "Disable" : "Enable"}
                        >
                          <IconButton
                            color={
                              account.manual_enabled ? "default" : "success"
                            }
                            onClick={() =>
                              onAction(
                                account,
                                account.manual_enabled ? "disable" : "enable",
                              )
                            }
                          >
                            <PowerSettingsNewIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function AccountDialog({
  value,
  onClose,
  onSaved,
}: {
  value: { category: AzureAccountCategory; account?: AzureAccount };
  onClose: () => void;
  onSaved: () => void;
}) {
  const account = value.account;
  const [form, setForm] = useState({
    name: account?.name ?? "",
    endpoint: account?.endpoint ?? "",
    azureKey: "",
    modelId: account?.model_id ?? "prebuilt-invoice",
    apiVersion: account?.api_version ?? "2024-11-30",
    monthlyCapacityPages:
      account?.monthly_capacity_pages ?? (value.category === "FREE" ? 500 : ""),
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const field =
    (name: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement>) =>
      setForm({ ...form, [name]: event.target.value });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {account ? "Edit" : "Add"} {value.category === "FREE" ? "Free" : "Paid"}{" "}
        Azure account
      </DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Friendly name"
            value={form.name}
            onChange={field("name")}
            required
          />
          <TextField
            label="Azure endpoint"
            value={form.endpoint}
            onChange={field("endpoint")}
            required
          />
          <TextField
            type="password"
            label={
              account
                ? "Replace API key (leave blank to preserve)"
                : "Azure API key"
            }
            value={form.azureKey}
            onChange={field("azureKey")}
            required={!account}
          />
          <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
            <TextField
              label="Model ID"
              value={form.modelId}
              onChange={field("modelId")}
              fullWidth
            />
            <TextField
              label="API version"
              value={form.apiVersion}
              onChange={field("apiVersion")}
              fullWidth
            />
          </Stack>
          <TextField
            type="number"
            label={
              value.category === "FREE"
                ? "Monthly page capacity"
                : "Optional monthly safety cap"
            }
            value={form.monthlyCapacityPages}
            onChange={field("monthlyCapacityPages")}
            helperText={
              value.category === "FREE"
                ? "Normally 500 for Azure F0"
                : "Leave blank for no application cap"
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={saving}
          onClick={async () => {
            setSaving(true);
            setError("");
            try {
              await api(
                account
                  ? `/admin/azure-accounts/${account.id}`
                  : "/admin/azure-accounts",
                {
                  method: account ? "PATCH" : "POST",
                  body: jsonBody({
                    category: value.category,
                    ...form,
                    monthlyCapacityPages:
                      form.monthlyCapacityPages === ""
                        ? null
                        : Number(form.monthlyCapacityPages),
                    azureKey: form.azureKey || undefined,
                  }),
                },
              );
              onSaved();
            } catch (e) {
              setError(
                e instanceof Error ? e.message : "Unable to save account",
              );
            } finally {
              setSaving(false);
            }
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function statusColor(
  status: string,
): "success" | "warning" | "error" | "default" {
  if (status === "ACTIVE") return "success";
  if (status === "COOLDOWN" || status === "QUOTA_EXHAUSTED") return "warning";
  if (status === "AUTH_FAILED" || status === "UNAVAILABLE") return "error";
  return "default";
}
function formatDate(value: string) {
  return new Date(value).toLocaleString();
}
